import React, { createContext, useContext, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import audioManager from '../services/audioManager'
import {
  Check,
  X,
  AlertTriangle,
  Info,
  Star,
  Coins,
  Hammer,
  ScrollText,
  Package,
  Trophy,
  PartyPopper,
  Gift
} from 'lucide-react'

const ToastContext = createContext()

const MAX_TOASTS = 5

// Toast type configurations
const TOAST_TYPES = {
  success: {
    icon: <Check size={20} />,
    bgClass: 'bg-gradient-to-r from-emerald-600 to-emerald-500',
    borderClass: 'border-emerald-400',
    glowColor: 'rgba(16, 185, 129, 0.4)',
    sound: 'success',
  },
  error: {
    icon: <X size={20} />,
    bgClass: 'bg-gradient-to-r from-red-600 to-red-500',
    borderClass: 'border-red-400',
    glowColor: 'rgba(239, 68, 68, 0.4)',
    sound: 'error',
  },
  warning: {
    icon: <AlertTriangle size={20} />,
    bgClass: 'bg-gradient-to-r from-amber-600 to-amber-500',
    borderClass: 'border-amber-400',
    glowColor: 'rgba(245, 158, 11, 0.4)',
    sound: 'warning',
  },
  info: {
    icon: <Info size={20} />,
    bgClass: 'bg-gradient-to-r from-blue-600 to-blue-500',
    borderClass: 'border-blue-400',
    glowColor: 'rgba(59, 130, 246, 0.4)',
    sound: 'open',
  },
  xp: {
    icon: <Star size={20} className="fill-purple-300" />,
    bgClass: 'bg-gradient-to-r from-purple-600 to-purple-500',
    borderClass: 'border-purple-400',
    glowColor: 'rgba(147, 51, 234, 0.5)',
    sound: 'success',
  },
  currency: {
    icon: <Coins size={20} className="text-yellow-300" />,
    bgClass: 'bg-gradient-to-r from-yellow-600 to-yellow-500',
    borderClass: 'border-yellow-400',
    glowColor: 'rgba(234, 179, 8, 0.5)',
    sound: 'coin',
  },
  craft: {
    icon: <Hammer size={20} />,
    bgClass: 'bg-gradient-to-r from-orange-600 to-orange-500',
    borderClass: 'border-orange-400',
    glowColor: 'rgba(249, 115, 22, 0.4)',
    sound: 'craft-complete',
  },
  quest: {
    icon: <ScrollText size={20} />,
    bgClass: 'bg-gradient-to-r from-slate-500 to-slate-400',
    borderClass: 'border-slate-300',
    glowColor: 'rgba(226, 232, 240, 0.3)',
    sound: 'success',
  },
  pack: {
    icon: <Package size={20} />,
    bgClass: 'bg-gradient-to-br from-pink-600 to-black',
    borderClass: 'border-pink-500',
    glowColor: 'rgba(236, 72, 153, 0.5)',
    sound: 'success',
    textColor: 'text-white',
  },
  achievement: {
    icon: <Trophy size={20} className="text-yellow-400" />,
    bgClass: 'bg-gradient-to-br from-blue-600 to-black',
    borderClass: 'border-blue-500',
    glowColor: 'rgba(59, 130, 246, 0.5)',
    sound: 'success',
    textColor: 'text-white',
  },
  daily_reward: {
    icon: <Gift size={20} className="text-white" />,
    bgClass: 'bg-gradient-to-r from-violet-600 to-indigo-600',
    borderClass: 'border-violet-400',
    glowColor: 'rgba(124, 58, 237, 0.5)',
    sound: 'success',
    textColor: 'text-white',
  },
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])

  const showToast = useCallback((message, type = 'success', options = {}) => {
    const id = Date.now() + Math.random()
    const config = TOAST_TYPES[type] || TOAST_TYPES.info
    const newToast = {
      id,
      message,
      type,
      config,
      duration: options.duration || 4000,
      ...options,
    }

    // Play sound for this toast type
    if (config.sound) {
      audioManager.play(config.sound)
    }

    setToasts((prev) => {
      const updated = [...prev, newToast]
      if (updated.length > MAX_TOASTS) {
        return updated.slice(-MAX_TOASTS)
      }
      return updated
    })

    // Auto-remove after duration
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id))
    }, newToast.duration)

    return id
  }, [])

  const success = useCallback((message, options) => {
    return showToast(message, 'success', options)
  }, [showToast])

  const error = useCallback((message, options) => {
    return showToast(message, 'error', options)
  }, [showToast])

  const warning = useCallback((message, options) => {
    return showToast(message, 'warning', options)
  }, [showToast])

  const info = useCallback((message, options) => {
    return showToast(message, 'info', options)
  }, [showToast])

  // Special toast for XP gains
  const xpGain = useCallback((amount, skillName) => {
    const message = skillName
      ? `+${amount} XP (${skillName})`
      : `+${amount} XP`
    return showToast(message, 'xp', { duration: 3000 })
  }, [showToast])

  // Special toast for batched XP gains (multiple skills)
  const xpGainBatch = useCallback((totalAmount, skillCount) => {
    const message = `+${totalAmount} XP in ${skillCount} skills`
    return showToast(message, 'xp', { duration: 3000 })
  }, [showToast])

  // Special toast for currency gains
  const currencyGain = useCallback((amount) => {
    return showToast(`+${amount} Currency`, 'currency', { duration: 3000 })
  }, [showToast])

  // Special toast for craft completion
  const craftComplete = useCallback((itemName) => {
    return showToast(`Crafted: ${itemName}`, 'craft', { duration: 4000 })
  }, [showToast])

  // Special toast for pack opening
  const packOpened = useCallback((packName, items) => {
    const itemsList = items
      .map((item) => `${item.quantity}x ${item.name}`)
      .join(', ')
    const message = `Opened ${packName}: ${itemsList}`
    return showToast(message, 'pack', { duration: 5000 })
  }, [showToast])

  // Special toast for achievement rewards
  const achievementRewardClaimed = useCallback((achievementName, currency, items) => {
    const rewardParts = []
    if (currency > 0) {
      rewardParts.push(`${currency} currency`)
    }
    if (items && items.length > 0) {
      const itemsList = items
        .map((item) => `${item.quantity}x ${item.name}`)
        .join(', ')
      rewardParts.push(itemsList)
    }

    const rewardText = rewardParts.length > 0 ? ` — ${rewardParts.join(' + ')}` : ''
    const message = (
      <div className="flex items-center gap-2">
        <PartyPopper size={18} className="text-yellow-400" />
        <span>{achievementName}{rewardText}</span>
      </div>
    )
    return showToast(message, 'achievement', { duration: 5000 })
  }, [showToast])

  // Special toast for daily rewards
  const dailyReward = useCallback((message) => {
    return showToast(message, 'daily_reward', { duration: 6000 })
  }, [showToast])

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{
      success,
      error,
      warning,
      info,
      xpGain,
      xpGainBatch,
      currencyGain,
      craftComplete,
      packOpened,
      achievementRewardClaimed,
      dailyReward,
      showToast,
      removeToast
    }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  )
}

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] flex flex-col items-center pb-6 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast, index) => (
          <Toast
            key={toast.id}
            toast={toast}
            onRemove={removeToast}
            index={index}
            total={toasts.length}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}

const Toast = React.forwardRef(({ toast, onRemove, index, total }, ref) => {
  const positionFromEnd = total - 1 - index

  // Carousel effect styling
  const opacity = Math.max(0.3, 0.95 - positionFromEnd * 0.15)
  const scale = Math.max(0.7, 1 - positionFromEnd * 0.08)
  const y = positionFromEnd * -8

  const config = toast.config || TOAST_TYPES.info

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: 50, scale: 0.8 }}
      animate={{
        opacity,
        y,
        scale,
      }}
      exit={{
        opacity: 0,
        x: 100,
        scale: 0.8,
        transition: { duration: 0.2 }
      }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30,
      }}
      className={`
        pointer-events-auto cursor-pointer
        ${config.bgClass}
        text-white px-5 py-3 rounded-lg
        mb-2 ${toast.type === 'pack' ? 'max-w-2xl' : 'max-w-md'} w-[92%]
        border-2 ${config.borderClass}
      `}
      style={{
        boxShadow: `0 10px 25px rgba(0, 0, 0, 0.3), 0 0 20px ${config.glowColor}`,
      }}
      onClick={() => onRemove(toast.id)}
      whileHover={{ scale: scale * 1.02, x: 5 }}
      whileTap={{ scale: scale * 0.98 }}
    >
      <div className="flex items-center gap-3">
        {/* Animated icon */}
        <motion.div
          className="text-2xl flex-shrink-0"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 15,
            delay: 0.1,
          }}
        >
          {config.icon}
        </motion.div>

        {/* Message */}
        <motion.div
          className="flex-1 font-semibold text-sm"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
        >
          {toast.message}
        </motion.div>

        {/* Progress bar for duration */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 rounded-b-lg overflow-hidden"
        >
          <motion.div
            className="h-full bg-white/40"
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{
              duration: toast.duration / 1000,
              ease: 'linear',
            }}
          />
        </motion.div>
      </div>
    </motion.div>
  )
})
